import { isNil } from "lodash"
import { toastError } from "../components/indeed-toast-container"
import { gnavFooterId } from "../constants/gnav"
import { IQueryParams } from "../models/query-params"
import { logDatadogRumAndThrow } from "./datadog"

export enum ReqStatus {
    READY,
    WAITING,
    FAILED,
    SUCCESS,
    LIMIT_REACHED,
}

export const Preconditions = {
    assertOrAlert(tst: boolean, msg: string) {
        if (!tst) alertAndThrow(msg, undefined)
    },
    assertOrAlertDatadogRum(tst: boolean, msg: string) {
        if (!tst) alertThrowAndLogDatadogRum(msg, undefined)
    },
    checkState: (tst: boolean, msg = "Illegal State") => {
        if (!tst) {
            throw Error(msg)
        }
    },
    checkNotNull: <T>(val?: T | null | undefined, msg = "value cannot be null, undefined, or NaN"): T => {
        if (val === null || val === undefined || Number.isNaN(Number(val))) {
            throw Error(msg)
        }
        return val
    },
}

/**
 * A more robust solution to printing the type of the object passed in
 *
 * @param {any} obj any object to get the typeof
 * @returns a string containing the type of the object (e.g. typeOf([]) => 'array')
 */
export const typeOf = (obj?: any): string => {
    const match = {}.toString.call(obj).match(/\s([a-z|A-Z]+)/)
    return match ? match[1].toLocaleLowerCase() : "null"
}

export const ifMobileOrLess = `@media (max-width: 500px)`
export const ifTabletOrLess = `@media (max-width: 800px)`
export const ifDesktopOrMore = `@media (min-width: 800px) and (min-height: 800px)`

/**
 * for use with filter, since ts can't infer non-null in a filter
 * @param {K} k key
 * @returns boolean if key exists
 */
export function hasKey<K extends string | number | symbol>(k: K) {
    return function <T, V>(a: T & { [k in K]?: V | null }): a is T & { [k in K]: V } {
        return a[k] !== undefined && a[k] !== null
    }
}

/**
 * For sane conditional render blocks in react components...
 */
export const ifTrue = <T>(condition: boolean, action: () => T, elseAction?: () => T) =>
    condition ? action() : elseAction ? elseAction() : undefined

/**
 * run a block of code. Useful for performing side effects in an inline expression or computing a value for a constant.
 * Example:
 * ```typescript
 * const myVal: string = apiResponse.maybeUndefinedValue ?? run(() => {
 *     console.warn("apiResponse had a missing value")
 *     return "some fallback value"
 * })
 *
 * // or
 * const numHamburgers = run(() => getFriendsCount() * computeAvgHamburgerPerPerson())
 * ```
 * @param {() => T} fn the block of code to run
 * @returns result of function execution
 */
export const run = <T>(fn: () => T): T => {
    return fn()
}

/**
 * Shows an alert to the user and throws an error stopping execution
 */
export const alertAndThrow = (err: string | Error, title?: string) => {
    const error = typeof err === "string" ? Error(err) : err
    toastError(error.message, title)
    throw error
}

/**
 * Shows an alert to the user and throws an error stopping execution
 * Logs error to Datadog
 */
export const alertThrowAndLogDatadogRum = (err: string | Error, title?: string) => {
    const error = typeof err === "string" ? Error(err) : err
    toastError(error.message, title)
    logDatadogRumAndThrow(error)
}

/**
 * Throw an error, stopping execution. Useful for terminating early on null data. For example:
 * ```js
 * const myValue = foo?.bar?.baz ?? throwError("no value at foo.bar.baz for")
 * ```
 * @param {string} msg message to include in error
 * @throws error including provided message
 */
export const throwError = (msg = "explicitly thrown error") => {
    throw new Error(msg)
}

/**
 * Log an error and return defaultValue
 */
export const defaultAndLogError = <T>(
    defaultValue: T,
    msg = `error encountered. Using default value: ${defaultValue}`,
    ...optionalParams: any[]
) => {
    console.error(msg, optionalParams)
    return defaultValue
}

/**
 * Tries to extract info and tk from the url query params.
 * @param urlParams
 */
export const parseEssentialQueryParams = (urlParams: URLSearchParams): Partial<IQueryParams> => {
    return {
        info: urlParams.get("info") || undefined,
        tk: urlParams.get("tk") || undefined,
        from: urlParams.get("from") || undefined,
    }
}

export function monkeyPatchLoggers() {
    const noopLogger = () => undefined
    const fallbackLogger = typeof window.console.log === "function" ? window.console.log : noopLogger
    if (typeof window.console.error !== "function") {
        window.console.error = (...msgs: any[]) => fallbackLogger("ERROR:", ...msgs)
    }
    if (typeof window.console.warn !== "function") {
        window.console.warn = (...msgs: any[]) => fallbackLogger("WARN:", ...msgs)
    }
}

export const toTitleCase = (rawTitle?: string): string =>
    (rawTitle || "")
        .split(" ")
        .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
        .join(" ")

export const displayFooter = () => {
    const gnavFooter = document.getElementById(gnavFooterId)

    if (!isNil(gnavFooter)) {
        gnavFooter.style.display = "block"
    }
}

export const hideFooter = () => {
    const gnavFooter = document.getElementById(gnavFooterId)

    if (!isNil(gnavFooter)) {
        gnavFooter.style.display = "none"
    }
}

export const setPageTitle = (title: string) => {
    document.title = title
}
